// src/lib/adminApi.js
import { useAuth0 } from '@auth0/auth0-react';

// normalize bases to avoid ...//Admin
const API_ROOT = (process.env.REACT_APP_BASE_URL || '').replace(/\/+$/, '');
const ADMIN_API_BASE = `${API_ROOT}/Admin`;

// If uploads are elsewhere, set REACT_APP_UPLOAD_BASE to that root.
// Otherwise we default to /Admin/uploads (matches our controller).
const UPLOAD_BASE = (process.env.REACT_APP_UPLOAD_BASE || ADMIN_API_BASE).replace(/\/+$/, '');

export function useAdminApi() {
  const { getAccessTokenSilently } = useAuth0();

  const getAuthHeader = async () => {
    try {
      const token = await getAccessTokenSilently();
      return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
      // API currently doesn't require JWT; continue without header in dev
      return {};
    }
  };

  const handleError = async (res) => {
    const text = await res.text().catch(() => '');
    let msg = text;
    try {
      const problem = JSON.parse(text);
      msg = problem?.detail || problem?.title || text;
    } catch {}
    throw new Error(`${res.status} ${res.statusText} - ${msg}`);
  };

  const authedFetch = async (path, options = {}) => {
    const auth = await getAuthHeader();
    const res = await fetch(`${ADMIN_API_BASE}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...auth,
        ...(options.headers || {}),
      },
    });
    if (!res.ok) return handleError(res);
    if (res.status === 204) return null;
    return res.json();
  };

  // ---- file upload with optional progress (XHR for progress, fetch otherwise)
  const upload = async (file, { path = 'images', onProgress } = {}) => {
    const auth = await getAuthHeader();
    const url = `${UPLOAD_BASE}/uploads?path=${encodeURIComponent(path)}`;
    const form = new FormData();
    form.append('file', file);

    if (typeof onProgress === 'function') {
      const responseText = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url);
        if (auth.Authorization) xhr.setRequestHeader('Authorization', auth.Authorization);
        xhr.upload.onprogress = (evt) => {
          if (evt.lengthComputable) onProgress(Math.round((evt.loaded * 100) / evt.total));
        };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300)
          ? resolve(xhr.responseText)
          : reject(new Error(`${xhr.status} ${xhr.statusText} - ${xhr.responseText}`));
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(form);
      });
      try { return JSON.parse(responseText)?.url; } catch { return responseText; }
    }

    const res = await fetch(url, { method: 'POST', headers: { ...auth }, body: form });
    if (!res.ok) return handleError(res);
    const data = await res.json();
    return data?.url;
  };

  return {
    get:  (path) => authedFetch(path, { method: 'GET' }),
    put:  (path, body) => authedFetch(path, { method: 'PUT',  body: JSON.stringify(body) }),
    post: (path, body) => authedFetch(path, { method: 'POST', body: JSON.stringify(body) }),
    del:  (path) => authedFetch(path, { method: 'DELETE' }),
    upload,
  };
}
